import { Config } from '@/appconfig/configUtils';
import { getSystem } from '@/system';
import { Profile } from '@/types';

import pkg from '../../package.json';
import { StoredDataKeyName, getStoredAccountDetails, getStoredProfile } from '../auth';

export enum MonitoringEventName {
  SIGNIN = 'signIn',
  SIGNOUT = 'signOut',
  VIDEOSTART = 'videoStart',
  SWITCHPROFILE = 'switchProfile',
  SIGNOUTREASON = 'SignOutReason',
  VIDEOSTARTSUCCESS = 'videoStartSuccess',
  VIDEOSTARTFAILURE = 'videoStartFailure',
  VIDEOPLAYBACKFAILURE = 'videoPlaybackFailure',
}

export enum CommonAttributeName {
  BMID = 'bmid',
  DEVICEID = 'deviceId',
  PROFILEID = 'profileId',
  ISMASTERPROFILE = 'isMasterProfile',
  MATURITY = 'maturity',
  BDUNAME = 'BDUName',
  ACCOUNTLANGUAGE = 'accountLanguage',
  TOKENSCOPE = 'tokenScope',
  LANGUAGE = 'language',
  SUBSCRIPTIONS = 'subscriptions',
  APPVERSION = 'appVersion',
  EXPERIENCESKU = 'experienceSKU',
}

export enum CustomAttributeName {
  SIGNOUTREASON = 'signOutReason',
  PROFILEUILANGUAGE = 'profileUILanguage',
  PLAYBACKLANGUAGE = 'playbackLanguage',
  MEDIAID = 'mediaId',
  CONTENTID = 'contentId',
  ERRORCODE = 'errorCode',
  ERRORMESSAGE = 'errorMessage',
}

export enum SignOutReason {
  USERSIGNEDOUT = 'User Signed Out through UI',
  TOKENMISSING = 'Token missing unexpectedly',
  USERACCOUNTINFORMATIONFAILED = 'Failed to fetch user account information',
  BDUSESSIONEXPIRED = 'BDU Session Expired',
  DTCSESSIONEXPIRED = 'DTC Session Expired',
}

const NEWRELIC_ACCOUNT_ID = process.env.NEXT_PUBLIC_NEWRELIC_ACCOUNT_ID || '';
const NEWRELIC_TRUST_KEY = process.env.NEXT_PUBLIC_NEWRELIC_TRUST_KEY || '';
const NEWRELIC_AGENT_ID = process.env.NEXT_PUBLIC_NEWRELIC_AGENT_ID || '';
const NEWRELIC_LICENSE_KEY = process.env.NEXT_PUBLIC_NEWRELIC_LICENSE_KEY || '';
const NEWRELIC_APPLICATION_ID = process.env.NEXT_PUBLIC_NEWRELIC_APPLICATION_ID || '';
const NEWRELIC_BEACON = process.env.NEXT_PUBLIC_NEWRELIC_BEACON || '';

const options = {
  init: {
    distributed_tracing: { enabled: true },
    privacy: { cookies_enabled: true },
    ajax: { deny_list: [NEWRELIC_BEACON] },
  }, // NREUM.init
  info: {
    beacon: NEWRELIC_BEACON,
    errorBeacon: NEWRELIC_BEACON,
    licenseKey: NEWRELIC_LICENSE_KEY,
    applicationID: NEWRELIC_APPLICATION_ID,
    sa: 1,
  }, // NREUM.info
  loader_config: {
    accountID: NEWRELIC_ACCOUNT_ID,
    trustKey: NEWRELIC_TRUST_KEY,
    agentID: NEWRELIC_AGENT_ID,
    licenseKey: NEWRELIC_LICENSE_KEY,
    applicationID: NEWRELIC_APPLICATION_ID,
  }, // NREUM.loader_config
};

let newRelicBrowserAgent: any = undefined;

// Passed straight to the agent's `log`, which drops the entry outright if the
// level is not one of its own. `warn`, not `warning`. @see LOG_LEVELS.
export type LogLevel = 'debug' | 'warn' | 'info' | 'error';

/**
 * Logs made before the agent exists, replayed by `init`.
 *
 * `init` is called from an effect in `App`, only once the config has loaded,
 * and then awaits a dynamic import before the agent exists at all. Anything
 * logging before that lands - a render-phase assertion, auth, the GraphQL
 * client, a failed fetch - would otherwise be dropped silently, which is the
 * whole reason those calls exist.
 */
let pendingLogs: { message: string; level: LogLevel }[] = [];

/**
 * Cap on the queue: with the `newRelic` flag off nothing ever drains it, and a
 * long-lived TV session logs for hours. The oldest lines are the ones worth
 * keeping - they are the ones no other mechanism could have caught - so the
 * queue stops accepting rather than evicting.
 */
const MAX_PENDING_LOGS = 50;

const version = pkg.version;

export async function init(config: Config): Promise<void> {
  const newRelicIsEnabled = !!config?.featureFlags?.newRelic;
  if (!newRelicIsEnabled) {
    // No agent is coming, so the queue would only grow.
    pendingLogs = [];
    return;
  }
  // The agent loader code executes immediately on instantiation.
  console.log('init called');
  const { BrowserAgent } = await import('@newrelic/browser-agent/loaders/browser-agent');
  newRelicBrowserAgent = new BrowserAgent(options);
  // set BMID before AppLoad so it is included in the event if the user is already authenticated
  const bmid = getStoredAccountDetails()?.id;
  if (bmid) {
    setCustomMonitoringAttribute(CommonAttributeName.BMID, bmid);
  }
  newRelicBrowserAgent?.recordCustomEvent('AppLoad');
  //new relic set app version
  setCustomMonitoringAttribute(CommonAttributeName.APPVERSION, version);

  // Last, so the replayed lines carry the attributes set above. They are
  // stamped with the flush time rather than their own - the agent's `log`
  // takes no timestamp - so they read a beat late, not lost.
  const queued = pendingLogs;
  pendingLogs = [];
  queued.forEach(({ message, level }) => log(message, level));
}

export function setCustomMonitoringAttribute(attributeName: string, value: any) {
  if (newRelicBrowserAgent) {
    newRelicBrowserAgent.setCustomAttribute(attributeName, value);
  } else {
    console.warn('New Relic not initialized');
  }
}

export function monitorEvent(eventName: MonitoringEventName, eventAttributes: any): void {
  if (newRelicBrowserAgent) {
    newRelicBrowserAgent?.recordCustomEvent(eventName, eventAttributes);
  } else {
    console.warn('New Relic not initialized');
  }
}

export function log(message: string, level: LogLevel): void {
  if (!newRelicBrowserAgent) {
    if (pendingLogs.length < MAX_PENDING_LOGS) pendingLogs.push({ message, level });
    return;
  }
  newRelicBrowserAgent.log(message, { level });
}

export const setCommonMonitoringAttributes = async (profile: Profile) => {
  setCustomMonitoringAttribute(CommonAttributeName.BMID, profile.accountId);
  setCustomMonitoringAttribute(CommonAttributeName.PROFILEID, profile.id);
  setCustomMonitoringAttribute(CommonAttributeName.BDUNAME, 'BDUName');

  const deviceId: string = await getSystem().getDeviceId();

  setCustomMonitoringAttribute(CommonAttributeName.DEVICEID, deviceId);
  setCustomMonitoringAttribute(CommonAttributeName.ISMASTERPROFILE, profile.master);

  const storedUserInformation = localStorage.getItem(StoredDataKeyName.USEREXPERIENCES);
  setCustomMonitoringAttribute(CommonAttributeName.SUBSCRIPTIONS, storedUserInformation);
  const experienceSKU = localStorage.getItem(StoredDataKeyName.EXPERIENCESKU);
  setCustomMonitoringAttribute(CommonAttributeName.EXPERIENCESKU, experienceSKU);

  const storedSubscriptionInformation = localStorage.getItem(
    StoredDataKeyName.SUBSCRIPTION
  );
  setCustomMonitoringAttribute(
    CommonAttributeName.BDUNAME,
    storedSubscriptionInformation ? storedSubscriptionInformation : ''
  );

  const storedProfile = getStoredProfile();

  setCustomMonitoringAttribute(
    CommonAttributeName.ACCOUNTLANGUAGE,
    `${profile.uiLanguage}-CA`
  );
  setCustomMonitoringAttribute(CommonAttributeName.LANGUAGE, storedProfile?.localization);
  setCustomMonitoringAttribute(CommonAttributeName.TOKENSCOPE, storedProfile?.scope);

  // Permutive
  window?.permutive?.identify([
    {
      id: profile.accountId,
      tag: 'bell_id',
    },
    {
      id: profile.id,
      tag: 'profile_id',
    },
  ]);
};
